import { DEPARTMENT_LABEL, type Department } from '@/domain';
import { strings } from '@/shared/strings';
import { SortIcon } from '@/ui/icons';
import styles from './AssigneeLine.module.css';

/**
 * A person in an assignment list: the name with how many cases they hold now
 * beside it, "Ahmed K. · 7 cases", and their department under it. Sections 7
 * and 8 of the request of 5 October 2026.
 *
 * One component for every list a case is given to somebody from: Assign, the
 * Mark done and Flag sheet, Send for rework, and assigning a selection. The
 * count is absent, not zero, when the list did not come with one.
 */
export function AssigneeLine({
  name,
  department,
  activeCases,
}: {
  name: string;
  department: Department;
  activeCases?: number | undefined;
}) {
  return (
    <span className={styles.who}>
      <span className={styles.top}>
        <span className={styles.name}>{name}</span>
        {activeCases !== undefined && (
          <span className={styles.count} data-testid="assignee-count">
            {' · '}
            {strings.workload.cases(activeCases)}
          </span>
        )}
      </span>
      <span className={styles.dept}>{DEPARTMENT_LABEL[department]}</span>
    </span>
  );
}

/**
 * Least busy first, on or off. Pressed, the people are ordered by how few cases
 * they hold; released, in the usual order, whoever works the department first.
 * A full 44 pixel target, and it says which way it is for a screen reader.
 */
export function LeastBusyToggle({ on, onChange }: { on: boolean; onChange: (on: boolean) => void }) {
  return (
    <button
      type="button"
      className={`${styles.toggle} ${on ? styles.toggleOn : ''}`}
      aria-pressed={on}
      onClick={() => onChange(!on)}
    >
      <SortIcon size={16} />
      {strings.workload.leastBusy}
    </button>
  );
}
